/** @format */

import {
  AnalyticsService,
  BreakpointsService,
  EmitEvent,
  EventBusService,
  Events,
  LocalStorageService,
  Scene7Service,
  UtilityService,
  WINDOW,
} from '@shared/data-access/services';
import { BreakPoint, BreakPointNew } from '@shared/data-access/enums';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DoCheck,
  IterableDiffer,
  IterableDiffers,
  OnInit,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformServer, isPlatformBrowser } from '@angular/common';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import {
  HmfButtonComponent,
  HmfCarouselComponent,
  HmfProductCardComponent,
  HmfSkeletonComponent,
} from '@dsg-tech/homefield-angular';
import {
  IElasticProductDetails,
  IElasticProductDetailsStyle,
  IRecommendationsRequestPurchaseTransaction,
  IRecommendationsResult,
  ProductRecommendationsPageType,
  ProductRecommendationsService,
} from '@product-recommendations/data-access';

import { ATC_BUTTON_TEXTS } from '@pdp/add-to-cart/data-access';
import { Chain } from '@shared/data-access/interfaces';
import { CheckoutPaymentPricing } from '@cart-checkout/data-access/interfaces';
import { reportQuickViewUnavailable } from '../recommendations-error-utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'shared-components-dsg-universal-recommendation',
  templateUrl: './dsg-universal-recommendation.component.html',
  styleUrl: './dsg-universal-recommendation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HmfButtonComponent,
    HmfCarouselComponent,
    HmfProductCardComponent,
    HmfSkeletonComponent,
  ],
})
export class DSGUniversalRecommendationComponent implements OnInit, DoCheck {
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW) as Window;
  private readonly chain = inject(Chain);
  private readonly iterableDiffers = inject(IterableDiffers);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly eventBusService = inject(EventBusService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly recommendationsService = inject(ProductRecommendationsService);
  private readonly scene7Service = inject(Scene7Service);
  private readonly utilityService = inject(UtilityService);
  private readonly breakpointsService = inject(BreakpointsService);
  private readonly platformId = inject(PLATFORM_ID);

  /*
   * INPUT(S)
   */
  readonly containerID = input('');
  readonly displayName = input('Recommendations');
  readonly dsRecsDiversityLevel = input('');
  readonly eCodeList = input<string[]>([]);
  readonly eventType = input('shopping-cart-page-view');
  readonly addPadding = input(false);
  readonly isUpperCase = input(true);
  readonly hideAddToCart = input(false);
  readonly hasGuidVisitor = input(false);
  readonly orderPricing = input<CheckoutPaymentPricing>();
  readonly placementID = input('frequently-bought-together-no-diversity');
  readonly pageType = input(ProductRecommendationsPageType.CART);
  readonly priceRerankLevel = input('no-price-reranking');
  readonly recommendationRefreshEnabled = input(false);
  readonly showViewAllLink = input(false);
  readonly enableRepositionRecommendations = input(false);
  readonly customLinkID = input('');
  readonly capitalizeTitle = input(false);
  readonly productRecommendationsQuickViewEnabled = input(false);
  readonly showHeading = input(true);
  readonly isPLA = input(false);
  readonly smallLegacyMobileCard = input(false);
  // HMF inputs
  readonly homefieldCarouselEnabled = input(false);
  readonly showHeader = input(false);
  readonly title = input('');
  readonly showSkeletonLoading = input(false);
  readonly cardSize = input<'small' | 'medium' | 'large'>();
  readonly dsgRecs = input('');
  readonly showScrollButtons = input(true);
  readonly showScrollButtonsOnMobile = input(false);
  readonly serverSideEnabled = input(false);

  /*
   * OUTPUT(S)
   */
  recommendationsLoaded = output<boolean>();
  recommendationsAvailable = output<boolean>();
  recommendationsErrored = output<void>();

  /*
   * Public instance properties
   */
  get showATC(): boolean {
    if (this.hideAddToCart()) {
      return false;
    }
    return (
      this.window.quickview?.openProductQuickView !== undefined &&
      this.window.quickview?.openProductQuickView !== null
    );
  }

  breakpoints = [BreakPoint.MEDIUM, BreakPoint.SMALL, BreakPoint.EXTRASMALL];
  eCodeListDiffer: IterableDiffer<string>;
  isFirstLoad = true;
  readonly isHydrated = signal(false);
  readonly shouldApplyPlaCarouselDefaultMargin = computed(
    () =>
      this.isPLA() &&
      this.homefieldCarouselEnabled() &&
      !this.shouldApplyPlaCarouselPreHydrationMargin(),
  );

  readonly shouldApplyPlaCarouselPreHydrationMargin = computed(
    () =>
      this.isPLA() &&
      this.homefieldCarouselEnabled() &&
      this.serverSideEnabled() &&
      !this.isHydrated(),
  );

  isMobile = computed(() => this.breakpointsService.breakpoint() <= BreakPointNew.MEDIUM);
  productRecommendations = signal<(IElasticProductDetailsStyle | null)[]>([]);
  belowMinimumRecommendations = signal(false);
  atcButtonTexts = signal(ATC_BUTTON_TEXTS);

  // The placementId resolved by the backend and returned on the response, used for RecID analytics tracking
  private readonly responsePlacementID = signal('');

  /*
   * Private instance properties
   */
  private athleteID: string | undefined;
  private readonly dsFilter = 'filterOutOfStockItems';
  private readonly dsRecsGCPProject = '';
  protected dsRecsProductDisplayLimit = signal(14);
  private readonly onAddToCartButtonClickEventName = 'QuickViewClicked';
  private readonly onRecommendationLinkClickEventName = 'RecommendationClicked';
  private readonly onRecommendationsDisplayErrorEventName = 'DCSG_NGX_CART_RECOMMENDATIONS_ERROR';
  constructor() {
    afterNextRender(() => {
      this.isFirstLoad = false;
      this.isHydrated.set(true);
    });
  }

  ngOnInit() {
    if (this.serverSideEnabled()) {
      if (!this.eCodeList()?.length) {
        return;
      }

      this.refreshRecommendations();
      return;
    }

    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (!this.eCodeList()?.length) {
      if (this.showSkeletonLoading() && this.isPLA()) {
        this.setRecsToEmptyForSkeletonLoading();
      }
      return;
    }

    if (this.recommendationRefreshEnabled()) {
      this.eCodeListDiffer = this.iterableDiffers.find([]).create(null);
    }

    this.athleteID = this.getAthleteID();

    if (this.showSkeletonLoading()) {
      this.setRecsToEmptyForSkeletonLoading();
    }

    this.refreshRecommendations();
  }

  /**
   * @description Set the recommendations to empty for skeleton loading
   * This will ensure that the product recommendations are not displayed until new data is received
   */
  setRecsToEmptyForSkeletonLoading(): void {
    // Set recs to nulls
    this.productRecommendations.set(new Array(8).fill(null));
  }

  // Listens for eCodeList changes to refresh on item removal or addition based on list content (not reference)
  ngDoCheck(): void {
    if (this.recommendationRefreshEnabled() && this.eCodeListDiffer) {
      const eCodeChanges = this.eCodeListDiffer.diff(this.eCodeList());
      if (!this.isFirstLoad && eCodeChanges && this.eCodeList()?.length > 0) {
        this.refreshRecommendations();
      }
    }
  }

  /**
   * @description
   * Event handler for when the ATC button is clicked in the product card. It will
   * trigger an Event to analytics and then open the QuickView panel.
   *
   * NOTE: This method can ONLY be called if QuickView is available, because the ATC button will not be visible
   * if QuickView is not available.
   *
   * @param style -- The recommended product style document
   */
  onAddToCartButtonClick(style: IElasticProductDetailsStyle, index: number) {
    // Trigger analytics Event
    this.analyticsService.triggerAnalyticsEvent(this.onAddToCartButtonClickEventName);
    this.openQuickView(style.partNumber, index);
  }

  /**
   * @description
   * Event handler for the QuickView "ATC Success Event".  It will emit an Event
   * on the Event Bus to be picked up by any listeners for additional processing.
   */
  onQuickViewAddToCartSuccess() {
    this.eventBusService.emit(new EmitEvent(Events.PostATCEvent));
  }

  /**
   * @description
   * Event handler for when the recommendation product card link is clicked. It will
   * trigger an Event to analytics and the re-assigns window.location
   *
   * @param style -- The recommended product style document
   * @param index -- The index location of the product in the list
   */
  onRecommendationLinkClick(style: IElasticProductDetailsStyle, index: number) {
    // Trigger analytics Event
    this.analyticsService.triggerAnalyticsEvent(this.onRecommendationLinkClickEventName);
    // Ensure the PDP URL exists
    if (typeof style.pdpSeoUrl === 'string' && style.pdpSeoUrl.trim() !== '') {
      if (
        this.pageType() === ProductRecommendationsPageType.CART &&
        this.productRecommendationsQuickViewEnabled()
      ) {
        this.openQuickView(style.partNumber, index);
        return;
      }
      // Build query params for analytics tracking
      const params = `recid=${this.buildRecID(this.pageType(), index)}`;
      // Assign window.location
      this.window.location.assign(
        `${style.pdpSeoUrl}${style.pdpSeoUrl.includes('?') ? '&' : '?'}${params}`,
      );
    }
  }

  mapItemToProductCardData(item: IElasticProductDetailsStyle) {
    if (!item) return null;
    return {
      assetSeoUrl: item.pdpSeoUrl,
      ecode: item.catentryId,
      imageData: {
        imageUrl: item.image,
      },
      pricing: {
        prices: {
          maxListPrice: item?.prices?.maxlistprice,
          maxOfferPrice: item?.prices?.maxofferprice,
          minListPrice: item?.prices?.minlistprice,
          minOfferPrice: item?.prices?.minofferprice,
          mapPriceIndicator: item?.prices?.mapPriceIndicator,
        },
        isSeePriceInCart:
          (!item?.prices?.minofferprice && !item?.prices?.maxofferprice) ||
          item?.prices?.mapPriceIndicator === 1,
      },
      productName: item.name,
    };
  }

  goToRecommendedForYouDS(): void {
    // Go to Recommended For You page /s/recommended-for-you
    this.window.location.href = '/s/recommended-for-you';
  }

  getProductCardSize(): 'small' | 'medium' | 'large' {
    if (this.cardSize()) {
      return this.cardSize();
    }
    if (this.chain.chainIdentifierAbbr === 'pl') return 'medium';
    return this.isMobile() ? 'small' : 'medium';
  }

  private openQuickView(partNumber: string, index: number) {
    if (!this.window.quickview?.openProductQuickView) {
      reportQuickViewUnavailable(
        this.analyticsService,
        this.onRecommendationsDisplayErrorEventName,
        this.pageType(),
      );
      return;
    }

    const dataAndCallBack = {
      additionalData: {
        DSDiversityLevel: this.dsRecsDiversityLevel(),
        DSDisplayName: this.displayName(),
        DSPlacementId: this.responsePlacementID(),
        DSPosition: index,
        DSPriceRerankLevel: this.priceRerankLevel(),
        DSRecsGCPProject: this.dsRecsGCPProject,
        customLinkID: this.customLinkID(),
      },
      callback: this.onQuickViewAddToCartSuccess.bind(this),
    };
    this.window.quickview.openProductQuickView(partNumber, 'DataScienceRecs', dataAndCallBack);
  }

  /**
   * @description
   * Checks localStorage to see if the AthleteID exists and will return it if so,
   * otherwise returns a default value of 'XXXXX'
   *
   * @private
   */
  private getAthleteID(): string | undefined {
    // The AthleteID to return, defaulted to the fallback value
    let athleteID = this.utilityService.generateUUID();
    if (this.hasGuidVisitor()) {
      return athleteID;
    }
    // Get the analytics IDs from localStorage
    const tagIDs: any = JSON.parse(this.localStorageService.get('TAG_IDs') || '{}');

    // Check to see if the AthleteID is actually set or if empty
    if (typeof tagIDs.AthleteID?.ID === 'string' && tagIDs.AthleteID.ID.trim() !== '') {
      athleteID = tagIDs.AthleteID.ID;
    } else if (typeof tagIDs.AdobeMCVID?.ID === 'string' && tagIDs.AdobeMCVID.ID.trim() !== '') {
      athleteID = tagIDs.AdobeMCVID.ID;
    }
    // Return the AthleteID
    return athleteID;
  }

  /**
   * @description
   * Does any product transformations before returning the results to be displayed in the template
   *
   * @param recommendations -- The recommendations to parse for display
   */
  private parseRecommendations(
    recommendations: IElasticProductDetails[],
  ): IElasticProductDetailsStyle[] {
    // Return array
    const returnRecommendations: IElasticProductDetailsStyle[] = [];

    // Iterate through the recommendations
    if (Array.isArray(recommendations) && recommendations?.length > 0) {
      recommendations.forEach((recommendation: IElasticProductDetails) => {
        // Get the default color if it is present
        const defaultColor =
          typeof recommendation.style.defaultColor === 'string' &&
          recommendation.style.defaultColor.trim() !== ''
            ? `${recommendation.style.defaultColor}`
            : '';

        // Set the image property on the style
        recommendation.style.image = this.scene7Service.getProductImageUrl(
          recommendation.style.partNumber,
          'itembean',
          defaultColor,
          '$UTPMain$',
          undefined,
          false,
        );

        recommendation.style.image = this.scene7Service.setImageWidthAndHeight(
          recommendation.style.image,
          500,
          500,
        );

        // Add to the style to the display list
        returnRecommendations.push(recommendation.style);
      });
    }

    // Return the recommendations
    return returnRecommendations;
  }

  private refreshRecommendations() {
    const purchaseTransaction = {} as IRecommendationsRequestPurchaseTransaction;
    if (this.eventType() === 'purchase-complete') {
      purchaseTransaction.id = this.orderPricing().external_key;
      purchaseTransaction.revenue = this.orderPricing().total;
      purchaseTransaction.currencyCode = this.orderPricing().currency;
    }

    // Get recommendations
    const requestOptions = {
      eCodeList: this.eCodeList(),
      placementId: this.placementID(),
      filter: this.dsFilter,
      priceRerankLevel: this.priceRerankLevel(),
      diversityLevel: this.dsRecsDiversityLevel(),
      eventType: this.eventType(),
      maxNumberOfProductsToReturn: this.dsRecsProductDisplayLimit(),
      project: this.dsRecsGCPProject,
      purchaseTransaction,
      pageType: this.pageType(),
      containerID: this.containerID(),
      dsgRecs: this.dsgRecs(),
      useTransferState: this.serverSideEnabled(),
      timeoutMs: this.serverSideEnabled()
        ? DCSG_CONSTANTS.timeouts.xxshort
        : DCSG_CONSTANTS.timeouts.short,
      ...(this.serverSideEnabled() ? {} : { athleteId: this.athleteID }),
    };
    const recommendations$ =
      this.recommendationsService.getRecommendationsWithProductDetails$(requestOptions);

    recommendations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: IRecommendationsResult) => this.applyRecommendations(result),
      error: () => this.handleRecommendationsError(),
    });
  }

  private applyRecommendations({
    results,
    belowMinimumRecommendations,
    placementId,
  }: IRecommendationsResult) {
    this.belowMinimumRecommendations.set(belowMinimumRecommendations);
    this.responsePlacementID.set(placementId ?? '');
    const recommendations = this.parseRecommendations(results);
    this.productRecommendations.set(recommendations);
    this.recommendationsAvailable.emit(recommendations.length > 0 && !belowMinimumRecommendations);
    this.recommendationsLoaded.emit(true);
  }

  private handleRecommendationsError(): void {
    this.productRecommendations.set([]);
    this.recommendationsAvailable.emit(false);
    this.recommendationsErrored.emit();

    if (isPlatformBrowser(this.platformId)) {
      const error = 'Error getting DS Recommendations, not displaying any recommendations';

      this.analyticsService.setErrorId(this.onRecommendationsDisplayErrorEventName);
      this.analyticsService.setErrorMessage(`CART_RECOMMENDATIONS:${error}`);

      this.analyticsService.triggerAnalyticsEvent(this.onRecommendationsDisplayErrorEventName, {
        ErrorMessage: `CART_RECOMMENDATIONS: ${error}`,
        PageType: this.pageType() || 'Cart',
      });
    }
  }

  private buildRecID(pageType: string, productIndex: number): string {
    const linkID = this.customLinkID()
      ? `DS_${pageType}_${this.customLinkID()}`
      : `DS_${pageType}_${this.responsePlacementID()}`;
    return `${linkID}_${productIndex}`;
  }

  protected trackByName(index: number, item: IElasticProductDetailsStyle | null): string {
    // Need to check for .name because skeleton loader requires null values and name wont exist on these values.
    return index.toString() + (item?.name ? item.name : '');
  }
}
